'use client'

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useClickOutside } from '../../hooks/useClickOutside'
import { useDropdownPosition } from './useDropdownPosition'

import { Box, chakra } from '@chakra-ui/react'

import { SearchIcon } from '../Icon'
import { TypeaheadDropdown } from './TypeaheadDropdown'
import { ColorScheme, searchInputStyles } from './TypeaheadSearch.styles'
import {
  DropdownItem,
  PrimaryAction,
  TypeaheadOption,
} from './TypeaheadSearch.types'

export type { ColorScheme }
export type { TypeaheadOption, PrimaryAction }

export type CheckboxOption = TypeaheadOption & {
  isSelected: boolean
  isIndeterminate?: boolean
}

export type TypeaheadSearchProps = {
  /** Options to match against the typed query. */
  options: (TypeaheadOption | string)[]
  /**
   * Action shown in the dropdown (e.g. "Create …").
   * Only rendered when the search box has a value.
   */
  primaryAction?: PrimaryAction
  /** Whether the primary action appears before or after matched results. Defaults to `'after'`. */
  primaryActionPosition?: 'before' | 'after'
  /**
   * Maximum number of matched results to show. Defaults to `5` for plain
   * options. For `checkboxOptions`, no limit is applied unless explicitly
   * provided.
   */
  maxResults?: number
  /** Small muted uppercase header shown above the matched results section (e.g. `'Select label'`). */
  resultsHeader?: string
  /** Text shown when the search box is empty and `showResultsWhenEmpty` is false. */
  emptyLabel?: string
  /** Text shown when the typed query has no matches. Defaults to `'No results'`. Pass `''` to suppress the no-match row (and the dropdown) entirely. */
  noMatchLabel?: string
  /** Text shown when `showResultsWhenEmpty` is true but the options array is empty. */
  noOptionsLabel?: string
  /** Show results even when the search box is empty. Defaults to `false`. */
  showResultsWhenEmpty?: boolean
  /** CSS width of the search input. Defaults to `'100%'`. */
  width?: string | number
  /** CSS height of the search input. Defaults to `36`. */
  height?: string | number
  /** Whether to show the magnifying glass icon. Defaults to `true`. */
  showIcon?: boolean
  /** Override the dropdown background color (e.g. to match a parent container). */
  dropdownBg?: string
  /** Override the text color of the results header. */
  headerColor?: string
  /** Placeholder text. Defaults to `'Search…'`. */
  placeholder?: string
  /** Called when the user selects an option. */
  onSelect?: (value: string) => void
  /** Called whenever the search query changes. */
  onQueryChange?: (query: string) => void
  /** Called when the user dismisses the search (Escape key or click outside). */
  onDismiss?: () => void
  /** Focus the input immediately on mount. */
  autoFocus?: boolean
  /** Maximum height of the dropdown list. When set, the list scrolls if items exceed it. */
  maxDropdownHeight?: string | number
  /** Maximum number of characters allowed in the search input. */
  maxCharacter?: number
  /** Warning message shown above the input when query length >= maxCharacter - 10. */
  tooLongLabel?: string
  /**
   * Color scheme to apply.
   * - `'dark'` (default): uses semantic tokens resolved by `DarkProvider`.
   * - `'light'`: uses primitive color tokens from the dark package palette,
   *   also resolved by `DarkProvider`'s `ChakraProvider`.
   */
  colorScheme?: ColorScheme
  className?: string
  /**
   * Multi-select checkbox options. When provided, these are rendered as
   * checkbox rows in the dropdown. The dropdown stays open on click.
   * Use alongside `onToggle` and `showResultsWhenEmpty`.
   */
  checkboxOptions?: CheckboxOption[]
  /**
   * Keeps checkbox options visible and searchable while preventing mouse and
   * keyboard selection changes. Plain typeahead options are unaffected.
   */
  isCheckboxReadOnly?: boolean
  /**
   * `'internal'` (default) filters `checkboxOptions` by label against the
   * typed query. Pass `'external'` when the caller already filters
   * server-side (e.g. via `onQueryChange`) — `checkboxOptions` is rendered
   * as-given. Required for any match that isn't a literal label substring
   * (e.g. a facility matched by its address): the internal filter would
   * otherwise drop a correct server result whose label doesn't contain the
   * typed text.
   */
  filterMode?: 'internal' | 'external'
  /**
   * Called when a checkbox option is toggled. The dropdown does NOT close.
   * Only relevant when `checkboxOptions` is provided.
   */
  onToggle?: (value: string) => void
  /**
   * Content rendered at the bottom of the dropdown (e.g. Cancel/Apply buttons).
   * Only rendered when the dropdown is open and there are items to show.
   */
  footerContent?: React.ReactNode
  /**
   * Content rendered at the top of the dropdown, above the results header
   * (e.g. a pinned select-all row). Not affected by the search query.
   * Only supported in checkbox (multi-select) mode.
   */
  headerContent?: React.ReactNode
  /**
   * Called when the user presses Enter while in checkbox (multi-select) mode.
   * Use this to trigger a bulk apply action.
   */
  onApply?: () => void
  /**
   * When true (default), the dropdown renders inline below the input.
   * Pass false to portal to document.body — needed inside overflow:hidden
   * containers such as TagGroup.
   */
  inline?: boolean
  /**
   * When true, the component stretches to fill its parent's height.
   * The dropdown expands to consume remaining vertical space.
   */
  fillHeight?: boolean
  /**
   * When true, the dropdown stays open regardless of input focus state.
   * Closing is handled solely by click-outside / onDismiss.
   */
  alwaysOpen?: boolean
  /**
   * When true, removes the dropdown's border, box-shadow, and border-radius.
   */
  flatDropdown?: boolean
  /** Badge text shown next to a checkbox option's label when `option.isDisabled` is true. Defaults to `'Disabled'`. */
  disabledBadgeLabel?: string
  /**
   * Shows a spinner row in the dropdown while results are being fetched. For
   * async searches where `options` arrive after `onQueryChange` — the spinner
   * replaces the results/no-match rows until loading completes.
   */
  isLoading?: boolean
}

function normalizeOption(opt: TypeaheadOption | string): TypeaheadOption {
  return typeof opt === 'string' ? { value: opt } : opt
}

function isSelectable(item: DropdownItem) {
  return (
    item.type === 'option' ||
    item.type === 'action' ||
    item.type === 'checkbox-option'
  )
}

export const TypeaheadSearch = forwardRef<HTMLDivElement, TypeaheadSearchProps>(
  function TypeaheadSearch(
    {
      options,
      primaryAction,
      primaryActionPosition = 'after',
      maxResults,
      resultsHeader,
      emptyLabel,
      noMatchLabel = 'No results',
      noOptionsLabel,
      showResultsWhenEmpty = false,
      width = '100%',
      height = 36,
      showIcon = true,
      dropdownBg,
      headerColor,
      placeholder = 'Search…',
      onSelect,
      onQueryChange,
      onDismiss,
      autoFocus = false,
      maxDropdownHeight,
      maxCharacter,
      tooLongLabel,
      colorScheme = 'dark',
      className,
      checkboxOptions,
      isCheckboxReadOnly = false,
      filterMode = 'internal',
      onToggle,
      footerContent,
      headerContent,
      inline = true,
      fillHeight = false,
      alwaysOpen = false,
      onApply,
      flatDropdown = false,
      disabledBadgeLabel,
      isLoading = false,
    },
    ref
  ) {
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(-1)

    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    const isOpenRef = useRef(false)
    isOpenRef.current = isOpen

    // The autoFocus DOM attribute can lose the focus race when the element
    // that triggered this mount (e.g. a focusable dropdown trigger) receives
    // native focus from the same click. Defer an imperative focus past that.
    useEffect(() => {
      if (!autoFocus) return
      const timer = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(timer)
      // Mount-only: autoFocus is not expected to change after mount.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const normalizedOptions = options.map(normalizeOption)

    // When checkboxOptions provided, filter by query for multi-select mode —
    // unless the caller already filtered server-side (filterMode
    // 'external'), in which case checkboxOptions is rendered as-given. Only
    // limit checkbox results when maxResults is explicitly provided —
    // multi-select consumers rely on the full list by default.
    const matchedCheckboxOptions = checkboxOptions
      ? query && filterMode === 'internal'
        ? checkboxOptions.filter((opt) =>
            (opt.label ?? opt.value).toLowerCase().includes(query.toLowerCase())
          )
        : checkboxOptions
      : null
    const filteredCheckboxOptions =
      matchedCheckboxOptions && maxResults !== undefined
        ? matchedCheckboxOptions.slice(0, maxResults)
        : matchedCheckboxOptions

    const plainMaxResults = maxResults ?? 5
    const filteredOptions: TypeaheadOption[] = query
      ? normalizedOptions
          .filter((opt) =>
            (opt.label ?? opt.value).toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, plainMaxResults)
      : showResultsWhenEmpty
      ? normalizedOptions.slice(0, plainMaxResults)
      : []

    const isPrimaryActionVisible = !!primaryAction && !!query

    const dropdownItems: DropdownItem[] = (() => {
      // Multi-select checkbox mode
      if (filteredCheckboxOptions !== null) {
        const headerContentItem: DropdownItem[] = headerContent
          ? [{ type: 'header-content' }]
          : []
        const headerItem: DropdownItem[] = resultsHeader
          ? [{ type: 'results-header' }]
          : []
        const cbItems: DropdownItem[] = filteredCheckboxOptions.map(
          (o): DropdownItem => ({
            type: 'checkbox-option',
            option: o,
            isSelected: o.isSelected,
            isIndeterminate: o.isIndeterminate,
          })
        )
        const noMatchItems: DropdownItem[] =
          filteredCheckboxOptions.length === 0 && !!noMatchLabel
            ? [{ type: 'no-match' }]
            : []
        const footerItem: DropdownItem[] = footerContent
          ? [{ type: 'footer' }]
          : []
        return [
          ...headerContentItem,
          ...headerItem,
          ...cbItems,
          ...noMatchItems,
          ...footerItem,
        ]
      }

      if (!query) {
        const headerItem: DropdownItem[] = resultsHeader
          ? [{ type: 'results-header' }]
          : []
        if (filteredOptions.length > 0) {
          return [
            ...headerItem,
            ...filteredOptions.map(
              (o): DropdownItem => ({ type: 'option', option: o })
            ),
          ]
        }
        if (showResultsWhenEmpty && noOptionsLabel)
          return [...headerItem, { type: 'no-options' }]
        if (emptyLabel) return [{ type: 'empty' }]
        return []
      }

      // Async fetch in flight: show a spinner in place of results/no-match.
      if (isLoading) return [{ type: 'loading' }]

      const headerItem: DropdownItem[] =
        resultsHeader && filteredOptions.length > 0
          ? [{ type: 'results-header' }]
          : []
      const optionItems: DropdownItem[] = filteredOptions.map((o) => ({
        type: 'option' as const,
        option: o,
      }))
      const noMatchItems: DropdownItem[] =
        filteredOptions.length === 0 && !primaryAction && !!noMatchLabel
          ? [{ type: 'no-match' }]
          : []

      if (!isPrimaryActionVisible)
        return [...headerItem, ...optionItems, ...noMatchItems]

      const actionItem: DropdownItem = { type: 'action' }
      return primaryActionPosition === 'before'
        ? [actionItem, ...headerItem, ...optionItems]
        : [...headerItem, ...optionItems, actionItem]
    })()

    const shouldShowDropdown =
      (isOpen || alwaysOpen) && dropdownItems.length > 0

    const dropdownPos = useDropdownPosition(
      shouldShowDropdown && !inline,
      inputRef,
      maxDropdownHeight
    )

    // Reset active index when query changes (dropdown may be open) or dropdown closes.
    useEffect(() => {
      setActiveIndex(-1)
    }, [query, isOpen])

    // Dismiss via Escape even when the input has lost focus (e.g. after clicking outside).
    useEffect(() => {
      if (!onDismiss) return
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !e.defaultPrevented) onDismiss()
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }, [onDismiss])

    const closeDropdown = useCallback(() => {
      if (!isOpenRef.current) return
      setIsOpen(false)
      onDismiss?.()
    }, [onDismiss])
    useClickOutside([containerRef, dropdownRef], closeDropdown)

    const handleSelectOption = useCallback(
      (value: string) => {
        onSelect?.(value)
        setQuery('')
        setIsOpen(false)
      },
      [onSelect]
    )

    // Checkbox option toggle — does NOT close the dropdown
    const handleToggleOption = useCallback(
      (value: string) => {
        if (isCheckboxReadOnly) return
        onToggle?.(value)
      },
      [isCheckboxReadOnly, onToggle]
    )

    const handlePrimaryAction = useCallback(() => {
      primaryAction?.onClick(query)
      setQuery('')
      setIsOpen(false)
    }, [primaryAction, query])

    const findSelectableIndex = useCallback(
      (from: number, direction: 1 | -1): number => {
        for (let i = from; i >= 0 && i < dropdownItems.length; i += direction) {
          if (isSelectable(dropdownItems[i]!)) return i
        }
        return -1
      },
      [dropdownItems]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
          case 'ArrowDown': {
            e.preventDefault()
            if (!isOpen) {
              setIsOpen(true)
              return
            }
            const next = findSelectableIndex(activeIndex + 1, 1)
            if (next !== -1) setActiveIndex(next)
            break
          }
          case 'ArrowUp': {
            e.preventDefault()
            const prev = findSelectableIndex(activeIndex - 1, -1)
            if (prev !== -1) setActiveIndex(prev)
            break
          }
          case 'Enter': {
            const item = dropdownItems[activeIndex]
            // In checkbox mode, Enter toggles the arrow-highlighted row;
            // with nothing highlighted it falls through to the bulk apply
            // action (BulkTypeaheadSearch's Enter-as-apply flow).
            if (item?.type === 'checkbox-option') {
              e.preventDefault()
              handleToggleOption(item.option.value)
              break
            }
            if (checkboxOptions !== undefined) {
              e.preventDefault()
              if (!isCheckboxReadOnly) onApply?.()
              break
            }
            if (item?.type === 'option') {
              e.preventDefault()
              handleSelectOption(item.option.value)
            } else if (item?.type === 'action') {
              e.preventDefault()
              handlePrimaryAction()
            }
            break
          }
          case 'Escape':
            e.preventDefault()
            setIsOpen(false)
            onDismiss?.()
            break
        }
      },
      [
        isOpen,
        activeIndex,
        checkboxOptions,
        isCheckboxReadOnly,
        dropdownItems,
        findSelectableIndex,
        handleSelectOption,
        handlePrimaryAction,
        handleToggleOption,
        onApply,
        onDismiss,
      ]
    )

    const primaryActionLabel = primaryAction
      ? typeof primaryAction.label === 'function'
        ? primaryAction.label(query)
        : primaryAction.label
      : ''

    function mergeRef(node: HTMLDivElement | null) {
      containerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    const isNearLimit = !!maxCharacter && query.length >= maxCharacter - 10

    return (
      <Box
        className={className}
        display='flex'
        flexDirection='column'
        ref={mergeRef}
        width={width}
        {...(fillHeight && { flex: 1, minHeight: 0 })}
      >
        {isNearLimit && !!tooLongLabel && (
          <Box color='red.400' fontSize='12px' mb='4px'>
            {tooLongLabel}
          </Box>
        )}
        <Box display='inline-flex' position='relative' width='100%'>
          {showIcon && (
            <Box
              alignItems='center'
              display='flex'
              left='10px'
              pointerEvents='none'
              position='absolute'
              top='50%'
              transform='translateY(-50%)'
            >
              <SearchIcon colorScheme={colorScheme} />
            </Box>
          )}
          <chakra.input
            autoFocus={autoFocus}
            maxLength={maxCharacter}
            onBlur={() => {
              if (!alwaysOpen) setIsOpen(false)
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setQuery(e.target.value)
              onQueryChange?.(e.target.value)
              setIsOpen(true)
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            onMouseDown={() => setIsOpen(true)}
            placeholder={placeholder}
            ref={inputRef}
            value={query}
            {...searchInputStyles(
              isOpen || alwaysOpen || !!query,
              colorScheme,
              height,
              showIcon
            )}
            {...(isNearLimit && { pr: '56px' })}
          />
          {isNearLimit && maxCharacter && (
            <Box
              as='span'
              color='red.400'
              fontSize='11px'
              pointerEvents='none'
              position='absolute'
              right='8px'
              top='50%'
              transform='translateY(-50%)'
            >
              ({query.length}/{maxCharacter})
            </Box>
          )}
        </Box>
        {shouldShowDropdown &&
          (inline || (dropdownPos && typeof document !== 'undefined')) && (
            <TypeaheadDropdown
              activeIndex={activeIndex}
              colorScheme={colorScheme}
              disabledBadgeLabel={disabledBadgeLabel}
              dropdownBg={dropdownBg}
              dropdownItems={dropdownItems}
              dropdownPos={
                dropdownPos ?? { top: 0, left: 0, width: 0, isOpenAbove: false }
              }
              dropdownRef={dropdownRef}
              emptyLabel={emptyLabel}
              fillHeight={fillHeight}
              flatDropdown={flatDropdown}
              footerContent={footerContent}
              handlePrimaryAction={handlePrimaryAction}
              handleSelectOption={handleSelectOption}
              handleToggleOption={handleToggleOption}
              headerColor={headerColor}
              headerContent={headerContent}
              inline={inline}
              isCheckboxReadOnly={isCheckboxReadOnly}
              maxDropdownHeight={maxDropdownHeight}
              noMatchLabel={noMatchLabel}
              noOptionsLabel={noOptionsLabel}
              primaryAction={primaryAction}
              primaryActionLabel={primaryActionLabel}
              primaryActionPosition={primaryActionPosition}
              resultsHeader={resultsHeader}
              setActiveIndex={setActiveIndex}
              showPrimaryAction={isPrimaryActionVisible}
            />
          )}
      </Box>
    )
  }
)
